import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

interface DemoToastValue {
  show: (message: string) => void;
}

const DemoToastContext = createContext<DemoToastValue>({ show: () => {} });

export function useDemoToast(): DemoToastValue {
  return useContext(DemoToastContext);
}

export default function DemoToastProvider({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState('');
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<number | null>(null);

  const show = useCallback((next: string) => {
    setMessage(next);
    setVisible(true);
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
    }
    timerRef.current = window.setTimeout(() => {
      setVisible(false);
    }, 2200);
  }, []);

  const value = useMemo(() => ({ show }), [show]);

  return (
    <DemoToastContext.Provider value={value}>
      {children}
      <div
        className={`fixed bottom-6 left-1/2 z-[80] w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 transition-all duration-300 ${
          visible ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-3 opacity-0'
        }`}
        aria-live="polite"
      >
        <div className="flex items-center gap-3 rounded-md border border-background-300/70 bg-foreground-950 px-4 py-3 text-background-50">
          <span className="flex h-5 w-5 items-center justify-center text-accent-400">
            <i className="ri-information-line text-base"></i>
          </span>
          <span className="text-sm leading-snug">{message}</span>
        </div>
      </div>
    </DemoToastContext.Provider>
  );
}